<script setup lang="ts">
import { isClient } from '@vueuse/core'
import ogImage from '@/assets/nepodcento/nepodcento_logo.png'
import SvgApple from '@/assets/nepodcento/apple_podcasts.svg?url'
import SvgSpotify from '@/assets/nepodcento/spotify_podcasts.svg?url'
import SvgGoogle from '@/assets/nepodcento/google_podcasts.svg?url'
import SvgYouRadio from '@/assets/nepodcento/youradio_podcasts.svg?url'
import SvgSeznam from '@/assets/nepodcento/seznam_podcasts.svg?url'
import type { NepodcentoPodcast } from '~/types/components/nepodcento'

const route = useRoute()
const slug = route.params.slug
let ogImageUrl = ''

function sanitazeStoryblokDate(date: string) {
  return date.replace(/-/g, '/')
}

const sbApi = useStoryblokApiV2()

const podcastContent: Pick<NepodcentoPodcast, 'content'>['content'] = await sbApi.getPage()
const podcasts = (await sbApi.getStories<NepodcentoPodcast>({
  by_slugs: ['pages/nepodcento/*'],
  sort_by: 'content.publishDate:desc',
  is_startpage: false,
  per_page: 8,
}))?.stories

if (isClient) ogImageUrl = `${window.location.host}${ogImage}`

const currentDate = new Date()
const olderPodcasts = podcasts.filter(
  item => new Date(sanitazeStoryblokDate(item.content.publishDate)) < currentDate && slug !== item.slug) || []

const breadcrumbs = [
  { slug: '/nepodcento', title: 'Podcast' },
]

useState('breadcrumbs').value = { title: podcastContent?.title, breadcrumbs }

onMounted(() => {
  window.scrollTo(0, 0)
})
const config = useRuntimeConfig()
const canonical = new URL(route.path, config.public.host)

useHead({
  title: podcastContent?.title,
  meta: [
    { property: 'description', content: podcastContent?.metaDescription },
    { property: 'og:image', content: ogImageUrl },
  ],
  link: [{
    rel: 'canonical',
    href: canonical.origin + canonical.pathname,
  }],
})
</script>

<template>
  <div class="podcast-detail">
    <div class="podcast-detail__background relative">
      <div class="container">
        <div class="w-full pt-3 md:pb-3 text-white">
          <AppBreadcrumbs />
        </div>
        <div class="flex flex-col-reverse md:flex-row md:mt-0">
          <div class="hidden md:block min-w-[280px] max-w-[350px]  mr-5">
            <StoryblokImage
              :src="podcastContent.thumbnail"
              height="280"
              class="text-white shadow-md rounded-xl hidden md:block xl:absolute lg:-bottom-12.5"
            />
          </div>
          <div class="w-full md:w-4/5 md:ml-8 md:min-h-60 md:flex md:flex-col md:justify-end">
            <div class="flex justify-between">
              <h1 class="text-white max-w-[50%] md:max-w-full">
                {{ podcastContent?.title }}
              </h1>
              <StoryblokImage
                :src="podcastContent.thumbnail"
                class="text-white shadow-md rounded-xl md:hidden max-h-[9em] sm:max-h-[15em]"
              />
            </div>

            <NepodcentoAudioPlayer
              :id="slug"
              :audio="podcastContent?.audio.filename"
              :title="podcastContent?.title"
              theme="light"
            />
            <div class="podcast-detail__podcasts">
              <StoryblokLink v-if="podcastContent?.applePodcast?.url" v-bind="podcastContent.applePodcast" blank>
                <img :src="SvgApple" height="40" width="166" alt="apple_podcast">
              </StoryblokLink>
              <StoryblokLink v-if="podcastContent?.spotifyPodcast?.url" v-bind="podcastContent.spotifyPodcast" blank>
                <img :src="SvgSpotify" height="40" width="122" alt="spotify_podcast">
              </StoryblokLink>
              <StoryblokLink v-if="podcastContent?.googlePodcast?.url" v-bind="podcastContent.googlePodcast" blank>
                <img :src="SvgGoogle" height="40" width="153" alt="google_podcast">
              </StoryblokLink>
              <StoryblokLink v-if="podcastContent?.youradioPodcast?.url" v-bind="podcastContent.youradioPodcast" blank>
                <img :src="SvgYouRadio" height="40" width="147" alt="youradio_podcast">
              </StoryblokLink>
              <StoryblokLink v-if="podcastContent?.seznamPodcast?.url" v-bind="podcastContent.seznamPodcast" blank>
                <img :src="SvgSeznam" height="40" width="147" alt="seznam_podcast">
              </StoryblokLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container md:flex">
      <div class="podcast-detail__banners">
        <NepodcentoProfileCard
          v-for="profileCard in podcastContent.profileCards"
          :image="profileCard.image"
          :header="profileCard.title"
          :text="profileCard.text"
          :links="profileCard.links[0]?.items"
          class="mb-5 max-w-330px"
        />
      </div>
      <div class="flex flex-col max-w-800px">
        <div class="podcast-detail__content">
          <StoryblokRichText :document="podcastContent?.text" />
        </div>

        <NepodcentoProfileCard
          v-for="profileCard in podcastContent.profileCards"
          :image="profileCard.image"
          :header="profileCard.title"
          :text="profileCard.text"
          :links="profileCard.links[0]?.items"
          class="md:hidden mb-5"
        />

        <div v-if="olderPodcasts?.length > 0" class="podcast-detail__next-episodes">
          <h2 class="podcast-detail__header">
            Další epizody
          </h2>
          <NepodcentoPodcastPreviewCard
            v-for="olderPodcast in olderPodcasts"
            :title="olderPodcast.content.title"
            :perex="olderPodcast.content.perex"
            :slug="olderPodcast.slug"
            :publish-date="olderPodcast.content.publishDate"
            :thumbnail="olderPodcast.content.thumbnail"
            :length="olderPodcast.content.audioLength"
            class="mb-4"
          />

          <div class="flex items-center">
            <StoryblokLink to="/nepodcento" class="podcast-detail__all-episodes">
              Všechny epizody
            </StoryblokLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang='scss' scoped>
.page {
  @apply bg-white pb-6;

  &__background {
    @apply w-full bg-white xl:bg-100;
    background-image: url("@/assets/nepodcento/bg-homepage.webp");
    background-repeat: no-repeat;
    background-position: top center;
    background-size: auto 30%;
  }

  &__container {
    @apply p-5 w-full relative;
  }

  &__moderator {
    background: url("@/assets/nepodcento/moderator.webp") no-repeat center;
    background-size: 100%;
    @apply h-80 small:h-70 small:bg-80 small:bg-top-center sm:h-65 sm:bg-40 sm:bg-top-center;
    @apply md:bg-100 md:bg-top-center md:right-0 md:h-130 md:top-20 md:absolute md:w-1/2;
    @apply lg:h-100 lg:w-13/30 lg:bg-xl xl:bg-60 bg-top bg-lg;
  }
}

.heading {
  @apply md:max-w-9/14 md:mt-12 md:h-110 lg:h-90;

  &__logo {
    @apply mb-4 h-10 sm:h-18;
    filter: invert(1);
  }

  &__text {
    @apply text-sm font-normal text-white leading-[22px] mb-4 md:text-base;
    ::v-deep(.podcast_image) {
      @apply inline-flex mr-2 mt-2 h-10;
    }
  }
}

.podcast-logo {
  @apply flex flex-wrap md:items-stretch;

  &__item {
    @apply my-2 mr-6 md:my-0;
  }
}

.container {
  @apply mx-auto max-w-[1192px] px-4;
}

.podcast-detail {
  @apply pb-15;

  &__podcasts {
    @apply flex pt-3;
    img {
      @apply pr-2 h-10;
    }
  }

  &__thumb {
    @apply shadow-md rounded-xl;
  }

  &__background {
    @apply w-full bg-cover bg-top bg-center pb-4 bg-no-repeat min-h-80;
    background-image: url("@/assets/nepodcento/bg-detail-page.webp");
  }

  &__banners {
    @apply mr-8 mt-20 min-w-[280px] hidden md:block;
  }

  &__content {
    @apply w-full mt-12 p-4 md:p-0;

    ::v-deep(*) {
      p {
        @apply pb-3 text-justify;
      }

      h1 {
        @apply text-4xl leading-10 md:text-4xl md:leading-10 mb-3;
      }

      h2 {
        @apply text-3xl mb-3;
      }

      h3 {
        @apply text-2xl mb-3;
      }

      h4 {
        @apply text-xl mb-3 mt-1;
      }

      ul {
        @apply list-disc m-0 pl-4 text-text;

        li {
          @apply leading-6;
        }
      }

      a {
        @apply underline text-secondary-active;
      }

      blockquote {
        @apply px-2 m-0;

        em {
          @apply text-lg font-semibold;
        }
      }
    }
  }

  &__header {
    @apply my-4;
  }

  &__all-episodes {
    @apply bg-warning text-white rounded-xl font-medium px-12 py-4 block m-auto mt-8;
  }
}
</style>
